import {intlShape, utils} from '@wallaby-ui/core';
import {isEmpty} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import {defineMessages, injectIntl} from 'react-intl';
import {connect} from 'react-redux';

import ArrayScreenWrapper from './ArrayScreenWrapper';
import {
  changeScreenProperty,
  getData,
  indexAddItem,
  indexRemoveItem,
  loadAutoLayout,
  resetCheckedIndexState,
  resetConflictLastChangeAuthor,
  saveIndex,
} from 'src/js/containers/auto_layout/auto_layout_actions';
import {
  getArrayWrapperIndex,
  getArrayWrapperLoadedIndex,
  getConflictLastChangeAuthor,
  getIndexChecking,
  isUnsavedScreenAdded,
} from 'src/js/containers/auto_layout/auto_layout_selectors';
import {I18N as SAVABLE_COMPONENT_I18N} from 'src/js/containers/auto_layout/components/SavableComponentContainer';
import AutoLayoutService from 'src/js/lib/auto_layout/auto_layout_service';
import {KEYWORD_DATA_ITEMS} from 'src/js/model/auto_layout_screen_data_helpers';

const I18N = defineMessages({
  ADDED_UNSAVED_SCREEN: {
    id: 'autoLayout.wrapper.addedUnSavedScreen',
    defaultMessage:
      'You already have an unsaved screen, save it first and then you can add another one.',
    description:
      'text for auto layout array container when trying to add multiple unsaved screens',
  },
});

class ArrayScreenWrapperContainer extends React.Component {
  constructor(props) {
    super(props);

    this.handleAdd = this.handleAdd.bind(this);
    this.handleAddedUnsavedScreenConfirm =
      this.handleAddedUnsavedScreenConfirm.bind(this);
    this.handleCancelConfirm = this.handleCancelConfirm.bind(this);
    this.handleReload = this.handleReload.bind(this);
    this.handleReloadConfirm = this.handleReloadConfirm.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.isItemLoaded = this.isItemLoaded.bind(this);
    this.performReload = this.performReload.bind(this);

    this.state = {
      showConfirm: false,
      confirmText: '',
      onConfirm: this.handleAddedUnsavedScreenConfirm,
    };
  }

  componentDidUpdate(prevProps) {
    const {indexChecking} = this.props;
    const {showConfirm} = this.state;
    if (
      indexChecking &&
      indexChecking !== prevProps.indexChecking &&
      !showConfirm
    ) {
      this.handleReload();
    }
  }

  handleAddedUnsavedScreenConfirm() {
    this.setState({
      showConfirm: false,
    });
  }

  handleCancelConfirm() {
    this.setState({
      showConfirm: false,
    });
  }

  handleAdd() {
    const {autoLayoutStateId, config, contextParams, intl, metadata, screenId} =
      this.props;
    if (!this.props.isUnsavedScreenAdded) {
      const itemId = utils.generateGUID();
      // generate new savable component (without change of index on server)
      this.props.indexAddItem(
        autoLayoutStateId,
        AutoLayoutService.getIndexScreenId(screenId, contextParams),
        itemId
      );
      const itemScreenId = AutoLayoutService.getItemScreenId(screenId, itemId);
      // load data of the added screen
      this.props.getData(
        autoLayoutStateId,
        config.service.url.autoLayout,
        itemScreenId,
        contextParams,
        utils.TranslateUtils.translate(SAVABLE_COMPONENT_I18N.ERROR_LOAD, intl),
        false,
        false,
        metadata.getContent(),
        metadata.getItemContent(),
        metadata.getItemLayout()
      );
      // turn the added screen into edit mode
      this.props.changeScreenProperty(autoLayoutStateId, itemScreenId, {
        isEditing: true,
        isCollapsed: false,
      });
    } else {
      this.setState({
        showConfirm: true,
        confirmText: utils.TranslateUtils.translate(
          I18N.ADDED_UNSAVED_SCREEN.id,
          intl
        ),
        onConfirm: this.handleAddedUnsavedScreenConfirm,
      });
    }
  }

  handleReloadConfirm() {
    this.handleCancelConfirm();
    this.performReload();
  }

  handleReload() {
    const {autoLayoutStateId, contextParams, intl, indexChecking, screenId} =
      this.props;
    if (
      indexChecking &&
      indexChecking.itemsDifferences &&
      (!isEmpty(indexChecking.itemsDifferences.added) ||
        !isEmpty(indexChecking.itemsDifferences.removed))
    ) {
      // show confirm dialog for index reloading
      this.setState({
        showConfirm: true,
        confirmText:
          indexChecking && indexChecking.invalidIndexError
            ? utils.TranslateUtils.translate(
                indexChecking.invalidIndexError,
                intl
              )
            : '',
        onConfirm: this.handleReloadConfirm,
      });
    } else if (!indexChecking.invokeInfo) {
      this.props.resetCheckedIndexState(
        autoLayoutStateId,
        AutoLayoutService.getIndexScreenId(screenId, contextParams)
      );
      this.props.resetConflictLastChangeAuthor(
        autoLayoutStateId,
        AutoLayoutService.getIndexScreenId(screenId, contextParams)
      );
    }
  }

  handleRemove(item) {
    const {autoLayoutStateId, contextParams, screenId} = this.props;
    const itemId = AutoLayoutService.getIdFromScreenId(item);
    const isItemLoaded = this.isItemLoaded(item);
    // remove item from index (without change of index on server)
    this.props.indexRemoveItem(
      autoLayoutStateId,
      AutoLayoutService.getIndexScreenId(screenId, contextParams),
      itemId
    );

    let removePromise;
    if (isItemLoaded) {
      // save removal from index on server
      removePromise = this.saveIndex(itemId, false);
      removePromise.catch(() => {
        // rollback removal of item from index
        this.props.indexAddItem(
          autoLayoutStateId,
          AutoLayoutService.getIndexScreenId(screenId, contextParams),
          itemId
        );
      });
    } else {
      removePromise = new Promise((resolve) => {
        resolve();
      });
    }
    return removePromise;
  }

  handleSave(item) {
    const {loadedIndex} = this.props;
    if (loadedIndex[KEYWORD_DATA_ITEMS] && !this.isItemLoaded(item)) {
      // save adding to index on server
      const itemId = AutoLayoutService.getIdFromScreenId(item);
      this.saveIndex(itemId, true);
    }
  }

  saveIndex(itemId, isAdded) {
    const {autoLayoutStateId, config, contextParams, intl, screenId} =
      this.props;
    // save index
    return this.props.saveIndex(
      autoLayoutStateId,
      config.service.url.autoLayout,
      screenId,
      contextParams,
      utils.TranslateUtils.translate(SAVABLE_COMPONENT_I18N.ERROR_SAVE, intl),
      itemId,
      isAdded
    );
  }

  performReload() {
    const {
      autoLayoutStateId,
      config,
      configStateId,
      screenId,
      contextParams,
      intl,
      indexChecking,
    } = this.props;
    const notSavedAddedScreens =
      indexChecking && indexChecking.currentIndexItemsDifferences.added
        ? [...indexChecking.currentIndexItemsDifferences.added]
        : [];

    if (
      indexChecking &&
      indexChecking.invokeInfo &&
      indexChecking.invokeInfo.invokerScreenId
    ) {
      this.props.resetConflictLastChangeAuthor(
        autoLayoutStateId,
        indexChecking.invokeInfo.invokerScreenId
      );
      this.props.resetCheckedIndexState(
        autoLayoutStateId,
        AutoLayoutService.getIndexScreenId(screenId, contextParams)
      );
    }

    this.props.loadAutoLayout(
      autoLayoutStateId,
      config,
      configStateId,
      contextParams,
      false,
      null,
      screenId,
      utils.TranslateUtils.translate(SAVABLE_COMPONENT_I18N.ERROR_LOAD, intl),
      true,
      notSavedAddedScreens
    );
  }

  isItemLoaded(item) {
    const {loadedIndex} = this.props;
    const itemId = AutoLayoutService.getIdFromScreenId(item);
    return (
      loadedIndex[KEYWORD_DATA_ITEMS] &&
      loadedIndex[KEYWORD_DATA_ITEMS].indexOf(itemId) !== -1
    );
  }

  render() {
    const {
      autoLayoutStateId,
      config,
      configStateId,
      conflictLastChangeAuthor,
      contextParams,
      errors,
      expanded,
      index,
      metadata,
      readOnly,
      screenId,
      screenProperties,
      screenStyles,
      textOnly,
    } = this.props;
    const {confirmText, showConfirm, onConfirm} = this.state;
    return (
      <ArrayScreenWrapper
        autoLayoutStateId={autoLayoutStateId}
        config={config}
        configStateId={configStateId}
        confirmText={confirmText}
        conflictLastChangeAuthor={conflictLastChangeAuthor}
        contextParams={contextParams}
        errors={errors}
        metadata={metadata}
        itemContent={metadata.getItemContent()}
        itemLayout={metadata.getItemLayout()}
        screenId={screenId}
        screenStyles={screenStyles}
        readOnly={readOnly}
        textOnly={textOnly}
        expanded={expanded}
        index={index}
        screenProperties={screenProperties}
        showConfirm={showConfirm}
        onAdd={this.handleAdd}
        onConfirm={onConfirm}
        onCancelConfirm={this.handleCancelConfirm}
        onSave={this.handleSave}
        onReload={this.performReload}
        onRemove={this.handleRemove}
        isItemLoaded={this.isItemLoaded}
      />
    );
  }
}

ArrayScreenWrapperContainer.displayName = 'ArrayScreenWrapperContainer';

ArrayScreenWrapperContainer.propTypes = {
  autoLayoutStateId: PropTypes.string.isRequired,
  config: PropTypes.objectOf(PropTypes.any).isRequired,
  configStateId: PropTypes.string.isRequired,
  contextParams: PropTypes.objectOf(PropTypes.any).isRequired,
  intl: intlShape.isRequired,
  metadata: PropTypes.objectOf(PropTypes.any).isRequired,
  screenId: PropTypes.string.isRequired,
  screenStyles: PropTypes.objectOf(PropTypes.any),
  readOnly: PropTypes.bool,
  textOnly: PropTypes.bool,
  expanded: PropTypes.bool,
};

ArrayScreenWrapperContainer.defaultProps = {
  readOnly: false,
  screenStyles: undefined,
  textOnly: false,
  expanded: false,
};

export default connect(
  (state, props) => ({
    conflictLastChangeAuthor: getConflictLastChangeAuthor(state, props),
    errors:
      state[props.autoLayoutStateId].errors[
        AutoLayoutService.getIndexScreenId(props.screenId, props.contextParams)
      ],
    indexChecking: getIndexChecking(state, props),
    loadedIndex: getArrayWrapperLoadedIndex(state, props),
    index: getArrayWrapperIndex(state, props),
    screenProperties: state[props.autoLayoutStateId].screenProperties,
    isUnsavedScreenAdded: isUnsavedScreenAdded(state, props),
  }),
  {
    changeScreenProperty,
    getData,
    indexAddItem,
    indexRemoveItem,
    loadAutoLayout,
    resetCheckedIndexState,
    resetConflictLastChangeAuthor,
    saveIndex,
  }
)(injectIntl(ArrayScreenWrapperContainer));
